import { useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { useParams } from 'react-router';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Typography } from 'common/components/Typography';
import {
    TokenLoginDocument,
    ResendUserEmailDocument,
    ITokenState,
    IUserEmailType,
    ITokenUserFragment,
} from 'graphql/types';
import { LoaderOverlay } from 'common/components/Loader';
import { Button } from 'common/components/Button';
import { Link } from 'common/components/Link';
import { PageTitle } from 'common/components/PageTitle';

interface IProps {
    forPage: 'resetPassword' | 'welcome';
    children(user: ITokenUserFragment): React.ReactNode;
}

export const TokenLogin = ({ forPage, children }: IProps) => {
    const { t: translate, i18n } = useTranslation();

    const { token } = useParams();

    const [tokenLogin, { data: tokenResponse, loading: loadingLogin, client }] =
        useMutation(TokenLoginDocument);

    const [resendUserEmail, { data: resendEmailData, loading: sendingEmail }] =
        useMutation(ResendUserEmailDocument);

    useEffect(() => {
        if (!token) return;

        // Clear any stale cached data before token login establishes new session
        client.clearStore().then(() => {
            tokenLogin({ variables: { token } });
        });
    }, [token, tokenLogin, client]);

    const { user: tokenUser, state: tokenState } =
        tokenResponse?.tokenLogin || {};
    const tokenIsValid = tokenState === ITokenState.Valid;
    const tokenIsInvalid = tokenState === ITokenState.Invalid;
    const tokenIsExpired = tokenState === ITokenState.Expired;

    const userLanguage = tokenUser?.language;

    useEffect(() => {
        if (!userLanguage) return;

        i18n.changeLanguage(userLanguage);
    }, [userLanguage, i18n]);

    const mailSent = resendEmailData?.resendUserEmail.ok || false;

    let transKey: string;
    let emailType: IUserEmailType;

    switch (forPage) {
        case 'resetPassword':
            transKey = 'tokenLogin.resetPassword';
            emailType = IUserEmailType.ResetPassword;

            break;
        case 'welcome':
            transKey = 'tokenLogin.welcome';
            emailType = IUserEmailType.Welcome;

            break;
    }

    return (
        <>
            <LoaderOverlay visible={loadingLogin}>
                {translate('loadingTexts.tokenLogin')}
            </LoaderOverlay>
            {!loadingLogin && tokenState && (
                <>
                    {/* Render children when token is valid */}
                    {tokenIsValid && tokenUser && children(tokenUser)}

                    {tokenIsInvalid && (
                        <>
                            <PageTitle>
                                {translate(`${transKey}.tokenInvalidPageTitle`)}
                            </PageTitle>
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                                <Typography variant="h2">
                                    {translate(`${transKey}.tokenInvalidTitle`)}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mt: 4,
                                }}
                            >
                                <Button
                                    color="primary"
                                    component={Link}
                                    to="LOGIN"
                                    variant="contained"
                                >
                                    {translate('gotoLogin')}
                                </Button>
                            </Box>
                        </>
                    )}

                    {tokenIsExpired && (
                        <>
                            <PageTitle>
                                {translate(`${transKey}.tokenExpiredPageTitle`)}
                            </PageTitle>
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                                <Typography variant="h2">
                                    {translate(`${transKey}.tokenExpiredTitle`)}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mt: 4,
                                }}
                            >
                                <Button
                                    color="primary"
                                    disabled={mailSent}
                                    loading={sendingEmail}
                                    variant="contained"
                                    onClick={() => {
                                        resendUserEmail({
                                            variables: {
                                                token: token || '',
                                                emailType,
                                            },
                                        });
                                    }}
                                >
                                    {mailSent
                                        ? translate('emailSent')
                                        : translate('sendNewLink')}
                                </Button>
                            </Box>
                        </>
                    )}
                </>
            )}
        </>
    );
};
